(function () {
  'use strict';

  class SunshadesSelector extends HTMLElement {
    connectedCallback() {
      const data = window.SunshadesData || { pieces: [], bundles: [] };
      this._pieces = this._parse(data.pieces);
      this._bundles = this._parse(data.bundles);
      this._handle = data.handle || '';
      this._sellingPlanId = data.sellingPlanId || null;

      // State
      this._selectedPieces = new Set(this._pieces.map(p => p.id)); // pre-select all
      this._currentTab = 'custom';

      // Auto-match a bundle if pre-selected pieces match one
      const initBundle = this._bundles.find(b => {
        const s = this._selectedPieces;
        return this._bundleAvailable(b) && b.pieces.length === s.size && b.pieces.every(pid => s.has(pid));
      });
      this._activeBundle = initBundle ? initBundle.id : null;

      this._renderCustom();
      this._updateTotal();
      this._bindEvents();
      this._initSvgViews();
      this._watchStoq();
    }

    _initSvgViews() {
      const wrap = this.querySelector('.snsh-svg-wrap');
      if (!wrap) return;

      const data = window.SunshadesData || {};
      const svgViews = Array.isArray(data.svgViews) ? data.svgViews : [];
      const productImages = window.SunshadesProductImages || [];

      const allViews = [
        ...svgViews.map(v => ({ type: 'svg', label: v.label, url: v.url, thumb: v.url })),
        ...productImages.map((img, i) => ({ type: 'image', label: img.alt || ('Photo ' + (i + 1)), url: img.url, thumb: img.thumb }))
      ];

      if (!allViews.length) { this._updateSvg(); return; }

      this._svgViewCount = allViews.length;
      this._svgViewIndex = 0;

      const multi = allViews.length > 1;

      // Render panels inside the white card
      wrap.insertAdjacentHTML('beforeend', `
        <div class="snsh-view-main">
          ${multi ? `<button class="snsh-view-arrow snsh-view-arrow--prev" aria-label="Previous view"><span class="visually-hidden">Previous</span><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="m6.797 11.625 8.03-8.03 1.06 1.06-6.97 6.97 6.97 6.97-1.06 1.06z"/></svg></button>` : ''}
          <div class="snsh-view-panels">
            ${allViews.map((v, i) => {
              const active = i === 0;
              if (v.type === 'image') {
                const repBadge = window.SunshadesVerifiedImages ? '' : '<span class="representative-badge">Representative Image</span>';
                return `<div class="snsh-view-panel snsh-view-panel--image${active ? ' is-active' : ''}" data-view-panel="${i}" role="tabpanel" aria-hidden="${!active}">
                  <img src="${this._esc(v.url)}" alt="${this._esc(v.label)}" width="1200" height="600" loading="${active ? 'eager' : 'lazy'}" decoding="async">
                  ${repBadge}
                </div>`;
              }
              return `<div class="snsh-view-panel snsh-view-panel--svg${active ? ' is-active' : ''}" data-view-panel="${i}" role="tabpanel" aria-hidden="${!active}"></div>`;
            }).join('')}
          </div>
          ${multi ? `<button class="snsh-view-arrow snsh-view-arrow--next" aria-label="Next view"><span class="visually-hidden">Next</span><svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="m9.693 4.5 7.5 7.5-7.5 7.5" stroke="currentColor" stroke-width="1.5" fill="none"/></svg></button>` : ''}
          ${multi ? `<div class="snsh-view-counter"><span class="snsh-view-counter__current">1</span> / ${allViews.length}</div>` : ''}
        </div>
      `);

      // Render thumbnail strip OUTSIDE the white card (after the wrap)
      if (multi) {
        wrap.insertAdjacentHTML('afterend', `
          <div class="snsh-view-thumbs">
            ${allViews.map((v, i) => `
              <button class="snsh-view-thumb${i === 0 ? ' is-active' : ''}" data-view-tab="${i}" aria-label="${this._esc(v.label) || 'View ' + (i + 1)}">
                <img src="${this._esc(v.thumb)}" alt="" loading="lazy">
              </button>
            `).join('')}
          </div>
        `);

        const prevBtn = wrap.querySelector('.snsh-view-arrow--prev');
        const nextBtn = wrap.querySelector('.snsh-view-arrow--next');
        prevBtn.addEventListener('click', () => {
          this._switchSvgView((this._svgViewIndex - 1 + this._svgViewCount) % this._svgViewCount);
        });
        nextBtn.addEventListener('click', () => {
          this._switchSvgView((this._svgViewIndex + 1) % this._svgViewCount);
        });
        // Set initial boundary state (first slide — prev is disabled)
        prevBtn.disabled = true;
        // Thumbs are now outside the wrap — query from this
        this.querySelector('.snsh-view-thumbs').addEventListener('click', (e) => {
          const btn = e.target.closest('[data-view-tab]');
          if (!btn) return;
          this._switchSvgView(parseInt(btn.dataset.viewTab, 10));
        });
      }

      // Fetch SVG panels in parallel
      // DOMParser ensures the SVG is a proper namespaced element (queryable via querySelector)
      // rather than an opaque HTML blob that renders but can't be traversed.
      allViews.forEach((view, i) => {
        if (view.type !== 'svg') return;
        fetch(view.url)
          .then(r => r.text())
          .then(svgText => {
            const panel = wrap.querySelector(`[data-view-panel="${i}"]`);
            if (!panel) return;

            const parser = new DOMParser();
            const doc = parser.parseFromString(svgText, 'image/svg+xml');
            const svgEl = doc.documentElement;

            panel.innerHTML = '';
            panel.appendChild(document.importNode(svgEl, true));

            this._updateSvg();
            this._renderBundles();
            this._renderCustom();
            this._autoSwitchView(Array.from(this._selectedPieces));
          });
      });
    }

    _switchSvgView(index) {
      this._svgViewIndex = index;

      // Thumbs are outside the wrap — query from this
      this.querySelectorAll('[data-view-tab]').forEach((btn, i) => {
        btn.classList.toggle('is-active', i === index);
      });

      const wrap = this.querySelector('.snsh-svg-wrap');
      if (!wrap) return;
      wrap.querySelectorAll('[data-view-panel]').forEach((panel, i) => {
        const active = i === index;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', !active);
      });

      const counter = wrap.querySelector('.snsh-view-counter__current');
      if (counter) counter.textContent = index + 1;

      // Blur arrows at boundaries
      const prev = wrap.querySelector('.snsh-view-arrow--prev');
      const next = wrap.querySelector('.snsh-view-arrow--next');
      if (prev) prev.disabled = (index === 0);
      if (next) next.disabled = (index === this._svgViewCount - 1);
    }

    _bindEvents() {
      // Tab clicks
      const tabRow = this.querySelector('.snsh-tab-row');
      if (tabRow) {
        tabRow.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-snsh-tab]');
          if (!btn) return;
          this._switchTab(btn.dataset.snshTab);
        });
      }

      // Bundle card clicks
      const bundleList = this.querySelector('.snsh-bundle-list');
      if (bundleList) {
        bundleList.addEventListener('click', (e) => {
          const card = e.target.closest('[data-bundle-id]');
          if (!card) return;
          this._selectBundle(card.dataset.bundleId);
        });
      }

      // Custom piece toggles
      const customList = this.querySelector('.snsh-custom-list');
      if (customList) {
        customList.addEventListener('click', (e) => {
          const card = e.target.closest('[data-piece-id]');
          if (!card) return;
          this._togglePiece(card.dataset.pieceId);
        });
      }

      // ATC button
      const atcBtn = this.querySelector('[data-snsh-atc]');
      if (atcBtn) {
        atcBtn.addEventListener('click', () => {
          this._addToCart();
        });
      }

      // SVG zone clicks (event delegation on the svg-wrap)
      const svgWrap = this.querySelector('.snsh-svg-wrap');
      if (svgWrap) {
        svgWrap.addEventListener('click', (e) => {
          const zone = e.target.closest('[data-svg-zone]');
          if (!zone) return;
          this._togglePiece(zone.dataset.svgZone);
        });

        svgWrap.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          const zone = e.target.closest('[data-svg-zone]');
          if (!zone) return;
          e.preventDefault();
          this._togglePiece(zone.dataset.svgZone);
        });
      }
    }

    // A piece is available for purchase when it has a real variant_id
    _pieceAvailable(piece) {
      return !!(piece && piece.variant_id && piece.variant_id !== 0);
    }

    // A bundle is available only when every piece in it is available
    _bundleAvailable(bundle) {
      return bundle.pieces.every(pid => {
        const p = this._pieces.find(pc => pc.id === pid);
        return this._pieceAvailable(p);
      });
    }

    // Returns all zone elements matching a piece across all SVG views.
    // Tries piece.id first; falls back to piece.sku so products whose SVG uses
    // SKU-style zone IDs (e.g. "rear-door") still match even when piece.id differs.
    _findZones(piece) {
      let els = Array.from(this.querySelectorAll(`[data-svg-zone="${CSS.escape(piece.id)}"]`));
      if (!els.length && piece.sku) {
        els = Array.from(this.querySelectorAll(`[data-svg-zone="${CSS.escape(piece.sku)}"]`));
      }
      return els;
    }

    // Same but scoped to a single root element (used for cloned SVGs in thumbnails).
    _findZone(root, piece) {
      let el = root.querySelector(`[data-svg-zone="${CSS.escape(piece.id)}"]`);
      if (!el && piece.sku) el = root.querySelector(`[data-svg-zone="${CSS.escape(piece.sku)}"]`);
      return el;
    }

    _selectBundle(id) {
      if (this._activeBundle === id) {
        // Deselect
        this._activeBundle = null;
        this._selectedPieces.clear();
      } else {
        this._activeBundle = id;
        this._selectedPieces.clear();
        const bundle = this._bundles.find(b => b.id === id);
        if (bundle) {
          bundle.pieces.forEach(pid => this._selectedPieces.add(pid));
          this._autoSwitchView(bundle.pieces);
        }
      }
      this._renderBundles();
      this._renderCustom();
      this._updateSvg();
      this._updateTotal();
    }

    _togglePiece(id) {
      const piece = this._pieces.find(p => p.id === id);
      if (!this._pieceAvailable(piece)) return; // coming soon — not interactive

      if (this._selectedPieces.has(id)) {
        this._selectedPieces.delete(id);
      } else {
        this._selectedPieces.add(id);
        this._autoSwitchView([id]);
      }

      // Re-match selection against bundles to preserve discount if pieces still match
      const matchingBundle = this._bundles.find(b => {
        const s = this._selectedPieces;
        return b.pieces.length === s.size && b.pieces.every(pid => s.has(pid));
      });
      this._activeBundle = matchingBundle ? matchingBundle.id : null;

      // Switch to custom tab if currently on bundles tab and no bundle matched
      if (this._currentTab === 'bundles' && !this._activeBundle) {
        this._switchTab('custom');
      }

      this._renderBundles();
      this._renderCustom();
      this._updateSvg();
      this._updateTotal();
    }

    _autoSwitchView(pieceIds) {
      const wrap = this.querySelector('.snsh-svg-wrap');
      if (!wrap) return;
      const panels = wrap.querySelectorAll('[data-view-panel]');
      if (panels.length <= 1) return;

      let bestIndex = this._svgViewIndex;
      let bestCount = -1;

      panels.forEach((panel, i) => {
        const svg = panel.querySelector('svg');
        if (!svg) return;
        let count = 0;
        pieceIds.forEach(pid => {
          const piece = this._pieces.find(p => p.id === pid);
          if (piece && this._findZone(svg, piece)) count++;
        });
        if (count > bestCount) { bestCount = count; bestIndex = i; }
      });

      if (bestCount > 0 && bestIndex !== this._svgViewIndex) {
        this._switchSvgView(bestIndex);
      }
    }

    _switchTab(tab) {
      this._currentTab = tab;

      const bundlePanel = this.querySelector('#snsh-panel-bundles');
      const customPanel = this.querySelector('#snsh-panel-custom');
      const tabs = this.querySelectorAll('[data-snsh-tab]');

      tabs.forEach(btn => {
        const isActive = btn.dataset.snshTab === tab;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      if (bundlePanel) {
        if (tab === 'bundles') {
          bundlePanel.removeAttribute('hidden');
        } else {
          bundlePanel.setAttribute('hidden', '');
        }
      }
      if (customPanel) {
        if (tab === 'custom') {
          customPanel.removeAttribute('hidden');
        } else {
          customPanel.setAttribute('hidden', '');
        }
      }
    }

    _renderBundles() {
      const list = this.querySelector('.snsh-bundle-list');
      if (!list) return;

      const availableBundles = this._bundles.filter(b => this._bundleAvailable(b));

      if (!availableBundles.length) {
        list.innerHTML = '<p style="font-size:0.8125rem;color:#888;text-align:center;padding:1rem 0;">No bundles configured.</p>';
        return;
      }

      list.innerHTML = availableBundles.map(bundle => {
        const isSelected = this._activeBundle === bundle.id;
        const fullPrice = this._fullPrice(bundle.pieces);
        const discPrice = this._discPrice(bundle);
        const savings = fullPrice - discPrice;
        const rawDisc = bundle.discount || 0;
        const savingsPct = rawDisc > 1 ? Math.round(rawDisc) : Math.round(rawDisc * 100);
        const includesNames = bundle.pieces
          .map(pid => {
            const p = this._pieces.find(pc => pc.id === pid);
            return p ? p.name : pid;
          })
          .join(', ');

        const popularBadge = bundle.popular
          ? '<span class="snsh-popular-badge">Most Popular</span>'
          : '';

        const wasPrice = bundle.discount && fullPrice > discPrice
          ? `<span class="snsh-bundle-was">${this._formatMoney(fullPrice)}</span>`
          : '';

        const saveLabel = bundle.discount && savings > 0
          ? `<span class="snsh-bundle-save">Save ${savingsPct}%</span>`
          : '';

        const thumbnail = this._makeThumbnail(bundle.pieces);

        return `
          <div
            class="snsh-bundle-card${isSelected ? ' is-selected' : ''}${bundle.popular && !isSelected ? ' is-popular' : ''}"
            data-bundle-id="${this._esc(bundle.id)}"
            role="radio"
            aria-checked="${isSelected}"
            tabindex="0"
          >
            ${popularBadge}
            ${thumbnail}
            <div class="snsh-bundle-content">
              <div class="snsh-bundle-name">${this._esc(bundle.name)}</div>
              <div class="snsh-bundle-includes">${this._esc(includesNames)}</div>
              <div class="snsh-bundle-price-row">
                <span class="snsh-bundle-price">${this._formatMoney(discPrice)}</span>
                ${wasPrice}
                ${saveLabel}
              </div>
            </div>
          </div>
        `;
      }).join('');

      // Re-bind keyboard on newly rendered cards
      list.querySelectorAll('[data-bundle-id]').forEach(card => {
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._selectBundle(card.dataset.bundleId);
          }
        });
      });
    }

    _renderCustom() {
      const list = this.querySelector('.snsh-custom-list');
      if (!list) return;

      if (!this._pieces.length) {
        list.innerHTML = '<p style="font-size:0.8125rem;color:#888;text-align:center;padding:1rem 0;">No pieces configured.</p>';
        return;
      }

      list.innerHTML = this._pieces.map(piece => {
        const available = this._pieceAvailable(piece);
        const isSelected = available && this._selectedPieces.has(piece.id);
        const checkMark = isSelected
          ? `<svg width="11" height="9" viewBox="0 0 11 9" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
               <polyline points="1,4.5 4,7.5 10,1.5" stroke="#1a1a0a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
             </svg>`
          : '';

        const thumbnail = this._makeThumbnail([piece.id]);
        const twoPieceWindscreens = ['ineos-grenadier-2023-present-sunshades', 'mercedes-benz-sprinter-2019-present-sunshades', 'mercedes-benz-sprinter-2006-2018-sunshades'];
        const isWindscreen = /windscreen|windshield/i.test(piece.name);
        const qtyLabel = /windows/i.test(piece.name) ? '(1 Pair)' : (isWindscreen && twoPieceWindscreens.includes(this._handle)) ? '(2 Pieces)' : '(1 Piece)';

        if (!available) {
          return `
            <div class="snsh-option-card snsh-option-card--unavailable" aria-disabled="true">
              ${thumbnail}
              <div class="snsh-option-info">
                <div class="snsh-option-name">${this._esc(piece.name)} <span class="snsh-qty-label">${qtyLabel}</span></div>
                ${piece.description ? `<div class="snsh-option-desc">${this._esc(piece.description)}</div>` : ''}
              </div>
              <div class="snsh-option-right">
                <span class="snsh-coming-soon-badge">Coming Soon</span>
              </div>
            </div>
          `;
        }

        return `
          <div
            class="snsh-option-card${isSelected ? ' is-selected' : ''}"
            data-piece-id="${this._esc(piece.id)}"
            role="checkbox"
            aria-checked="${isSelected}"
            tabindex="0"
          >
            ${thumbnail}
            <div class="snsh-option-info">
              <div class="snsh-option-name">${this._esc(piece.name)} <span class="snsh-qty-label">${qtyLabel}</span></div>
              ${piece.description ? `<div class="snsh-option-desc">${this._esc(piece.description)}</div>` : ''}
              ${piece.sku ? `<div class="snsh-option-sku">${this._esc(piece.sku)}</div>` : ''}
            </div>
            <div class="snsh-option-right">
              <span class="snsh-option-price">${this._formatMoney(piece.price)}</span>
              <div class="snsh-option-check" aria-hidden="true">${checkMark}</div>
            </div>
          </div>
        `;
      }).join('');

      // Re-bind keyboard on newly rendered cards
      list.querySelectorAll('[data-piece-id]').forEach(card => {
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._togglePiece(card.dataset.pieceId);
          }
        });
      });
    }

    _updateSvg() {
      this._pieces.forEach(piece => {
        const available = this._pieceAvailable(piece);
        const isSelected = available && this._selectedPieces.has(piece.id);

        this._findZones(piece).forEach(zone => {
          // Unavailable zones: suppress pointer events, use neutral grey
          zone.style.cursor = available ? 'pointer' : 'default';
          zone.style.pointerEvents = available ? '' : 'none';

          let fillEl = piece.svg_fill_id ? zone.querySelector(`#${CSS.escape(piece.svg_fill_id)}`) : null;
          if (!fillEl) fillEl = zone.querySelector('rect, path, polygon');
          if (!fillEl && zone.matches('rect, path, polygon')) fillEl = zone;

          if (fillEl) {
            if (!available) {
              fillEl.setAttribute('fill', '#888888');
              fillEl.setAttribute('fill-opacity', '0.10');
            } else {
              fillEl.setAttribute('fill', isSelected ? '#C9E500' : '#3b82f6');
              fillEl.setAttribute('fill-opacity', isSelected ? '0.55' : '0.12');
            }
          }

          let labelEl = piece.svg_label_id ? zone.querySelector(`#${CSS.escape(piece.svg_label_id)}`) : null;
          if (!labelEl) labelEl = zone.querySelector('text, tspan');
          if (labelEl) labelEl.setAttribute('fill', available ? (isSelected ? '#3a4a00' : '#666') : 'transparent');
        });
      });
    }

    _updateTotal() {
      const selectedIds = Array.from(this._selectedPieces);
      let total = 0;
      let savings = 0;

      if (this._activeBundle) {
        const bundle = this._bundles.find(b => b.id === this._activeBundle);
        if (bundle) {
          const full = this._fullPrice(bundle.pieces);
          total = this._discPrice(bundle);
          savings = full - total;
        }
      } else {
        total = this._fullPrice(selectedIds);
        savings = 0;
      }

      const priceEl = this.querySelector('.snsh-total-price');
      if (priceEl) priceEl.textContent = this._formatMoney(total);

      const originalEl = this.querySelector('.snsh-total-original');
      if (originalEl) {
        if (savings > 0) {
          const full = this._fullPrice(selectedIds);
          originalEl.textContent = this._formatMoney(full);
          originalEl.style.display = '';
        } else {
          originalEl.textContent = '';
          originalEl.style.display = 'none';
        }
      }

      const badgeEl = this.querySelector('.snsh-savings-badge');
      if (badgeEl) {
        if (savings > 0) {
          badgeEl.textContent = `You save ${this._formatMoney(savings)}`;
          badgeEl.style.display = 'inline-block';
        } else {
          badgeEl.style.display = 'none';
          badgeEl.textContent = '';
        }
      }

      const atcBtn = this.querySelector('[data-snsh-atc]');
      if (atcBtn) {
        const hasSelection = selectedIds.length > 0;
        atcBtn.disabled = !hasSelection;
        const atcLabel = 'Add to Cart';
        const shipSuffix = this._stoqShipText ? ` – ${this._stoqShipText}` : '';
        atcBtn.textContent = hasSelection
          ? `${atcLabel}${shipSuffix} — ${this._formatMoney(total)}`
          : 'Select at least 1 item';
      }

      // Incentive nudges
      const bundleNudge = this.querySelector('.snsh-nudge--bundle');
      if (bundleNudge) {
        const textEl = bundleNudge.querySelector('.snsh-nudge__text');
        // Show bundle nudge when pieces are selected but don't match a bundle
        const hasSelection = selectedIds.length > 0;
        const bestBundle = this._bundles.find(b => this._bundleAvailable(b) && b.discount);
        if (hasSelection && !this._activeBundle && bestBundle) {
          const disc = bestBundle.discount > 1 ? Math.round(bestBundle.discount) : Math.round(bestBundle.discount * 100);
          const missing = bestBundle.pieces.filter(pid => !this._selectedPieces.has(pid));
          const missingNames = missing.map(pid => {
            const p = this._pieces.find(pc => pc.id === pid);
            return p ? p.name : pid;
          });
          textEl.textContent = `Add ${missingNames.join(', ')} for ${disc}% off the full set`;
          bundleNudge.style.display = '';
        } else {
          bundleNudge.style.display = 'none';
        }
      }

      const shippingNudge = this.querySelector('.snsh-nudge--shipping');
      if (shippingNudge) {
        const textEl = shippingNudge.querySelector('.snsh-nudge__text');
        const FREE_SHIPPING_THRESHOLD = 100;
        if (total > 0 && total < FREE_SHIPPING_THRESHOLD) {
          const remaining = FREE_SHIPPING_THRESHOLD - total;
          textEl.textContent = `Add ${this._formatMoney(remaining)} more for free shipping`;
          shippingNudge.style.display = '';
        } else {
          shippingNudge.style.display = 'none';
        }
      }

      // Sync FBT block with current piece selection
      const variantIds = selectedIds
        .map(pid => { const p = this._pieces.find(pc => pc.id === pid); return p ? p.variant_id : null; })
        .filter(Boolean);
      document.dispatchEvent(new CustomEvent('on:sunshades:selection', {
        bubbles: true,
        detail: { variantIds, total }
      }));
    }

    _fullPrice(ids) {
      return ids.reduce((sum, pid) => {
        const piece = this._pieces.find(p => p.id === pid);
        return sum + (piece ? (piece.price || 0) : 0);
      }, 0);
    }

    _discPrice(bundle) {
      const full = this._fullPrice(bundle.pieces);
      let discount = bundle.discount || 0;
      if (discount > 1) discount = discount / 100;
      return full * (1 - discount);
    }

    async _addToCart() {
      const atcBtn = this.querySelector('[data-snsh-atc]');
      const errorEl = this.querySelector('.snsh-error');

      // Hide previous error
      if (errorEl) {
        errorEl.classList.remove('is-visible');
        errorEl.textContent = '';
      }

      // Build items array
      let items = [];
      const activeBundle = this._activeBundle
        ? this._bundles.find(b => b.id === this._activeBundle)
        : null;

      if (activeBundle) {
        if (activeBundle.variant_id) {
          // Single bundle variant
          const item = {
            id: activeBundle.variant_id,
            quantity: 1,
            properties: { _bundle: activeBundle.name }
          };
          if (this._sellingPlanId) item.selling_plan = this._sellingPlanId;
          items.push(item);
        } else {
          // Individual pieces with bundle tag
          activeBundle.pieces.forEach(pid => {
            const piece = this._pieces.find(p => p.id === pid);
            if (!piece) return;
            if (!piece.variant_id) {
              console.warn('[SunshadesSelector] Missing variant_id for piece:', pid);
              return;
            }
            const item = {
              id: piece.variant_id,
              quantity: 1,
              properties: { _bundle: activeBundle.id }
            };
            if (this._sellingPlanId) item.selling_plan = this._sellingPlanId;
            items.push(item);
          });
        }
      } else {
        // Custom selection
        Array.from(this._selectedPieces).forEach(pid => {
          const piece = this._pieces.find(p => p.id === pid);
          if (!piece) return;
          if (!piece.variant_id) {
            console.warn('[SunshadesSelector] Missing variant_id for piece:', pid);
            return;
          }
          const item = {
            id: piece.variant_id,
            quantity: 1
          };
          if (this._sellingPlanId) item.selling_plan = this._sellingPlanId;
          items.push(item);
        });
      }

      if (items.length === 0) {
        if (errorEl) {
          errorEl.textContent = 'No variant IDs configured \u2014 contact support.';
          errorEl.classList.add('is-visible');
        }
        return;
      }

      // Show loading state
      if (atcBtn) atcBtn.classList.add('is-loading');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          let message = 'Could not add to cart. Please try again.';
          try {
            const data = await response.json();
            if (data && data.description) message = data.description;
            else if (data && data.message) message = data.message;
          } catch (_) {}
          throw new Error(message);
        }

        // Success — refresh cart and redirect
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        window.location.href = '/cart';
      } catch (err) {
        if (errorEl) {
          errorEl.textContent = err.message || 'Could not add to cart. Please try again.';
          errorEl.classList.add('is-visible');
        }
      } finally {
        if (atcBtn) atcBtn.classList.remove('is-loading');
      }
    }

    _makeThumbnail(pieceIds) {
      // Try variant image first (assigned in Shopify admin)
      const variantImages = window.SunshadesVariantImages || {};
      if (pieceIds.length === 1) {
        const piece = this._pieces.find(p => p.id === pieceIds[0]);
        if (piece && piece.variant_id && variantImages[piece.variant_id]) {
          return `<div class="snsh-card-thumb"><img src="${this._esc(variantImages[piece.variant_id])}" alt="${this._esc(piece.name)}" loading="lazy"></div>`;
        }
      }

      // Fallback: SVG-based thumbnail
      const panels = this.querySelectorAll('[data-view-panel]');
      let bestSvg = null;
      let bestCount = -1;
      panels.forEach(panel => {
        const el = panel.querySelector('svg');
        if (!el) return;
        let count = 0;
        pieceIds.forEach(pid => {
          const piece = this._pieces.find(p => p.id === pid);
          if (piece && this._findZone(el, piece)) count++;
        });
        if (count > bestCount) { bestCount = count; bestSvg = el; }
      });

      if (!bestSvg) return '<div class="snsh-card-thumb snsh-card-thumb--empty"></div>';

      const svg = bestSvg.cloneNode(true);
      svg.removeAttribute('id');
      svg.setAttribute('aria-hidden', 'true');

      const activeSet = new Set(pieceIds);

      // Reset every known zone, then highlight only the active ones
      this._pieces.forEach(piece => {
        const zone = this._findZone(svg, piece);
        if (!zone) return;
        const available = this._pieceAvailable(piece);
        const isActive = available && activeSet.has(piece.id);
        let fillEl = piece.svg_fill_id ? zone.querySelector(`#${CSS.escape(piece.svg_fill_id)}`) : null;
        if (!fillEl) fillEl = zone.querySelector('rect, path, polygon');
        if (!fillEl && zone.matches('rect, path, polygon')) fillEl = zone;
        if (fillEl) {
          if (!available) {
            fillEl.setAttribute('fill', '#888888');
            fillEl.setAttribute('fill-opacity', '0.08');
          } else {
            fillEl.setAttribute('fill', isActive ? '#C9E500' : '#3b82f6');
            fillEl.setAttribute('fill-opacity', isActive ? '0.6' : '0.06');
          }
        }
        let labelEl = piece.svg_label_id ? zone.querySelector(`#${CSS.escape(piece.svg_label_id)}`) : null;
        if (!labelEl) labelEl = zone.querySelector('text, tspan');
        if (labelEl) labelEl.setAttribute('fill', isActive ? '#3a4a00' : 'transparent');
      });

      const wrap = document.createElement('div');
      wrap.className = 'snsh-card-thumb';
      wrap.appendChild(svg);
      return wrap.outerHTML;
    }

    _watchStoq() {
      // STOQ injects into the hidden product-form by finding .product-info__add-to-cart .btn--primary.
      // It modifies the button text (e.g. "Add to Cart – Ships Oct 27") and adds a
      // .restock-rocket-preorder-description div. We mirror the shipping text into our real ATC.
      const hook = this.querySelector('.snsh-stoq-hook');
      if (!hook) return;

      const grab = () => {
        const stoqBtn = hook.querySelector('.restock-rocket-preorder-button');
        const stoqDesc = hook.querySelector('.restock-rocket-preorder-description');
        let shipText = '';
        if (stoqBtn) {
          const match = stoqBtn.textContent.match(/Ships?\s+.+/i);
          if (match) shipText = match[0].trim();
        }
        if (!shipText && stoqDesc) {
          const match = stoqDesc.textContent.match(/Ships?\s+.+/i);
          if (match) shipText = match[0].trim();
        }
        if (shipText && shipText !== this._stoqShipText) {
          this._stoqShipText = shipText;
          this._updateTotal();
        }
      };

      const observer = new MutationObserver(grab);
      observer.observe(hook, { childList: true, subtree: true, characterData: true, attributes: true });
      let attempts = 0;
      const poll = setInterval(() => {
        grab();
        if (++attempts > 30 || this._stoqShipText) clearInterval(poll);
      }, 500);
    }

    _parse(val) {
      if (Array.isArray(val)) return val;
      if (typeof val === 'string') { try { return JSON.parse(val); } catch(e) { return []; } }
      return [];
    }

    _formatMoney(amount) {
      return '$' + Number(amount || 0).toFixed(2);
    }

    _esc(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  if (!customElements.get('sunshades-selector')) {
    customElements.define('sunshades-selector', SunshadesSelector);
  }
})();

